import { alpha, createTheme, lighten, Theme } from "@mui/material/styles";
import { colorsCromatic } from "../colors";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    mdl: true; // Add the custom breakpoint
    lg: true;
    xl: true;
  }
  type CromaticColors = {
    green: ColorShades;
    blue: ColorShades;
    yellow: ColorShades;
    orange: ColorShades;
    red: ColorShades;
    purple: ColorShades;
    pink: ColorShades;
    grey: ColorShades;
  };

  type ColorShades = {
    darker: string;
    dark: string;
    main: string;
    light: string;
    lighter: string;
  };
  interface Palette {
    acess: {
      administrator: string;
      director: string;
      commercial: string;
      operational: string;
      service: string;
      owner: string;
    };
    cromatic: CromaticColors;
    table?: string;
    stroke?: string;
    alert: { success: string; info: string; error: string; warning: string };
  }
  interface PaletteOptions {
    acess?: {
      administrator: string;
      director: string;
      commercial: string;
      operational: string;
      service: string;
      owner: string;
    };
    cromatic?: CromaticColors;
    table?: string;
    stroke?: string;
    alert: { success: string; info: string; error: string; warning: string };
  }
  interface TypeText {
    charts: string; // Add the "charts" property to TypeText
    inverted: string; // Add the "inverted" property to TypeText
  }

  interface TypeBackground {
    textField: string; // Add the "textField" property to TypeBackground
    card: string;
  }
  interface PaperPropsVariantOverrides {
    default: true; // Add the "default" variant
  }
}

const colors = {
  primary: {
    light: "#13C06A",
    dark: "#1AD866",
  },
  secondary: {
    light: "#039FDD",
    dark: "#039FDD",
  },
};

const mainTheme = (colorMode: boolean): Theme =>
  createTheme({
    typography: {
      fontFamily: `'Lato'`,
    },
    shape: {
      borderRadius: 8,
    },
    palette: {
      mode: colorMode ? "dark" : "light",
      primary: {
        main: colorMode ? colors.primary.dark : colors.primary.light,
      },
      secondary: {
        main: colorMode ? colors.secondary.dark : colors.secondary.light,
      },
      background: {
        default: colorMode ? "#141A21" : "#e5e9ec",
        paper: colorMode ? "#1D242C" : "#FFFFFF",
        textField: colorMode ? "#303C48" : "#DBDBDB",
        card: colorMode ? "#171E27" : "#EEEEF1",
      },
      text: {
        primary: colorMode ? "#FFFFFF" : "#1C252E",
        secondary: colorMode ? "#919EAB" : "#637381",
        disabled: colorMode ? "#637381" : "#919EAB",
        inverted: !colorMode ? "#FFFFFF" : "#1C252E",
        charts: colorMode ? "#FFFFFF" : "#637381",
      },
      success: {
        main: colorMode ? "#22C55E" : "#04B55D",
      },
      warning: {
        main: colorMode ? "#EE9919" : "#EE9919",
      },
      error: {
        main: colorMode ? "#FF5630" : "#EE3737",
      },
      info: {
        main: colorMode ? "#00B8D9" : "#006C9C",
      },
      acess: {
        administrator: colorMode ? "#C684FF" : "#5119B7",
        director: colorMode ? "#68CDF9" : "#0351AB",
        commercial: colorMode ? "#61F4D9" : "#009E91",
        operational: colorMode ? "#FDAB76" : "#B75D1F",
        service: colorMode ? "#FF9582" : "#B3200E",
        owner: colorMode ? "#FFD666" : "#CC7E00",
      },
      alert: {
        success: colorMode ? "#1AD879" : "#8CFDB2",
        error: colorMode ? "#FF4444" : "#FF9582",
        info: colorMode ? "#00B8D9" : "#66CAF5",
        warning: colorMode ? "#FFBA2E" : "#F7D973",
      },
      cromatic: colorsCromatic(colorMode),
      table: colorMode ? alpha("#303C48", 0.5) : alpha("#DBDBDB", 0.29),
      stroke: colorMode ? "#343F4D" : "#DCDEE1",
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        mdl: 1024,
        lg: 1200,
        xl: 1536,
      },
    },
    components: {
      MuiTabs: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& button": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.background.default, 0.4)
                  : alpha(theme.palette.background.card, 0.5),
              color: theme.palette.text.secondary,
              textTransform: "none",
              borderRadius: "10px",
            },
            "& button.Mui-selected": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? theme.palette.background.default
                  : theme.palette.background.card,
              color: theme.palette.text.primary,
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTabs-list": {
              gap: 5,
            },
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            width: "100%",

            "& .MuiFilledInput-root": {
              backgroundColor: theme.palette.background.textField,
              borderRadius: "10px",
              fontSize: "0.875rem",
              "&:hover": {
                backgroundColor: lighten(
                  theme.palette.background.textField,
                  0.1
                ),
              },

              "& input:-webkit-autofill": {
                boxShadow: `0 0 0 1000px transparent inset`,
                transition: "background-color 5000s ease-in-out 0s",
                border: "none",
                lineHeight: "1.5",
                fontSize: "0.875rem",
              },
              //remove a linha
              "&:before, &:hover:before, &:after": {
                borderBottom: "none !important",
              },
            },
          }),
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "contained" }, // Define a nova variante "default"
            style: {
              height: "35px",
              backgroundColor: colorMode ? "#FFFFFF" : "#1C252E",
            },
          },
          {
            props: { variant: "outlined" }, // Define a nova variante "default"
            style: {
              height: "35px",
              color: !colorMode ? "#1C252E" : "#FFFFFF",
              border: `1px solid ${colorMode ? "#919EAB" : "#919EAB"}`,
              // secondary: colorMode ? "#919EAB" : "#637381",
              // disabled: colorMode ? "#637381" : "#919EAB",
              main: colorMode ? colors.secondary.dark : colors.secondary.light,
              //backgroundColor: colorMode ? "#FFFFFF" : "#1C252E",
            },
          },
          {
            props: { variant: "text" }, // Define a nova variante "default"
            style: {
              height: "35px",
              color: colorMode ? "#FFFFFF" : "#1C252E",
              paddingLeft: 15,
              paddingRight: 15,
            },
          },
        ],
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.inverted,
            textTransform: "capitalize",
            boxShadow: "none",
            borderRadius: "10px",
            "&:hover": {
              boxShadow: "none",
            },
            // backgroundColor:  colorMode ? "#FFFFFF" : "#1C252E",
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: "0px",
            borderBottom:
              "1px solid " +
              (colorMode ? alpha("#FFFFFF", 0.1) : alpha("#000000", 0.1)),
          },
          colorPrimary: {
            backgroundColor: colorMode ? "#141A21" : "#F4F6F8",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colorMode ? "#141A21" : "#F4F6F8",
            borderRadius: "0px",
          },
        },
      },
      MuiPaper: {
        variants: [
          {
            props: { variant: "outlined", elevation: 3 }, // Define a nova variante "default"
            style: {
              borderRadius: "1rem",
              border: "none",
              boxShadow: "0px 1px 2px  #0000001a",
            },
          },
        ],
        defaultProps: {
          variant: "outlined", // Define a variante padrão como outlined
        },
        styleOverrides: {
          root: {
            borderRadius: "1rem",
            border: "none",
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiLinearProgress-bar2Buffer": {
              backgroundColor: theme.palette.background.textField,
            },
          }),
        },
      },
    },
    mixins: {
      /*   MuiDataGrid: {
        containerBackground: !colorMode ? "#fff" : "#1E1E1E",
      },  */
    },
  });

export default mainTheme;
